import { useContext, useEffect, useRef } from "react"
import { useApi } from "auth/useApi3"
import { Autocomplete, Box, FormControl, Stack, TextField, Typography, useTheme } from "@mui/material"
import { useTranslation } from "react-i18next"
import { StyledFormLabel } from "shared/styled/Global"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { apiEndpoints } from "generated/apiEndpoints"
import {
  BasicUser,
  IFormUserPicker
} from "components/Form/FormComponents/FormUserPicker/FormUserPicker.interfaces"
import { AppContext } from "components/AppContextProvider"

const extractUserId = (value: any): number | string | null => {
  if (!value) return null

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value)
      return parsed?.Id || parsed?.id || null
    } catch {
      return value
    }
  }
  if (typeof value === "object" && value !== null) {
    return value?.Id || value?.id || null
  }

  return value
}

const FormUserPicker = (props: IFormUserPicker) => {
  const { id, value, handleChange, labelKey, labelDefault, optional, error, helperText } = props
  const appContext = useContext(AppContext)
  const [userListResult, userListApi] = useApi() as [IApiResult, IApiFunc]
  const theme = useTheme()
  const { t } = useTranslation()
  const hasPrefilled = useRef(false)

  const fallbackOptions = appContext.appUser?.profile?.id
    ? [{ id: appContext.appUser.profile.id, name: appContext.appUser.profile.name }]
    : []
  const options = userListResult?.data?.length ? userListResult.data : fallbackOptions

  const valueId = extractUserId(value)
  const autocompleteValue = options.find((user: BasicUser) => user?.id?.toString() === valueId?.toString()) || null

  useEffect(() => {
    const url = apiEndpoints.customeradmin.getcustomeractiveusers
    userListApi.get(url)
  }, [])

  useEffect(() => {
    const hasNotBeenPrefilled = !hasPrefilled.current
    const hasOptions = userListResult?.status === 2 && userListResult?.data
    const isValueEmpty = value === null || value === undefined || value === ""
    const canHandleChange = handleChange !== undefined

    if (hasNotBeenPrefilled && hasOptions && isValueEmpty && canHandleChange) {
      const loggedInUser = userListResult.data.find(
        (user: BasicUser) => user?.id?.toString() === appContext.appUser.profile.id.toString()
      )

      if (loggedInUser && handleChange) {
        handleChange(null, loggedInUser.id)
        hasPrefilled.current = true
      }
    } else if (hasNotBeenPrefilled && canHandleChange && isValueEmpty && appContext.appUser?.profile?.id) {
      handleChange(null, appContext.appUser?.profile?.id)
      hasPrefilled.current = true
    }
  }, [userListResult, value, appContext.appUser?.profile?.id, handleChange])

  return (
    <Box my={0.75}>
      <StyledFormLabel>
        <Typography variant="labelLarge" fontWeight={500}>
          {t(labelKey || "", { defaultValue: labelDefault })} {!optional && "*"}
        </Typography>
      </StyledFormLabel>
      <FormControl fullWidth>
        <Autocomplete
          loading
          loadingText={t("global.loading", "Loading") + "..."}
          sx={{ width: "100%", background: theme.palette.background.paper }}
          id={id?.toString()}
          options={options}
          getOptionLabel={(option: BasicUser) => option?.name || option?.Name || ""}
          onChange={(_, option) => handleChange && handleChange(_, option?.id)}
          includeInputInList
          value={autocompleteValue}
          renderOption={(props, option) => (
            <li {...props}>
              <Stack direction="row" spacing={2} justifyContent="space-between">
                <Box sx={{ fontWeight: 600, minWidth: "100px" }}>{option?.name}</Box>
                <Box sx={{ opacity: 0.6 }}>{option?.username}</Box>
              </Stack>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              placeholder={t("global.noone", "No one")}
              {...params}
              name={id?.toString()}
              variant="outlined"
              error={error}
              helperText={helperText}
            />
          )}
        />
      </FormControl>
    </Box>
  )
}

export default FormUserPicker
